import { useCallback } from "react";
import { ScenarioModel } from "../../clients/KompasApi";
import {
  Collapse,
  Table,
  Tbody,
  Td,
  Tr,
  useDisclosure,
} from "@chakra-ui/react";
import { useAtom } from "jotai";
import {
  highlightDifferencesAtom,
  onlyShowDifferencesAtom,
} from "../../pages/ComparePage";
import {
  extractValue,
  hasAnyMeasure,
  hasMeasure,
} from "../../shared/utils/measureUtils";
import { CategoryHeader } from "./CategoryHeader";
import { CategoryFooter } from "./CategoryFooter";
import { FixedNumberDisplay } from "../../elements/NumberDisplay";
import { TaxKey } from "../TaxSelect";
import { useComparePageContext } from "../../shared/state/ComparePageContext";

export const tempCalculateCO2Impact = (scenario: ScenarioModel) =>
  scenario.calculation.yearlyCO2Emission /
  (scenario.ventilationSystem?.id || 1);
export const tempCalculatePrice = (scenario: ScenarioModel) =>
  (scenario.ventilationSystem?.id ?? 0) * 1000;

export function VentilationMeasuresSubTable() {
  const { scenarios, situation } = useComparePageContext();
  const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: true });

  const [onlyShowDifferences] = useAtom(onlyShowDifferencesAtom);
  const [highlightDifferences] = useAtom(highlightDifferencesAtom);

  const scenarioValuesDiffer = useCallback(
    (measure: keyof ScenarioModel) =>
      new Set(scenarios.map((scenario) => extractValue(scenario[measure])))
        .size > 1,
    [scenarios],
  );

  return (
    <Collapse
      in={hasAnyMeasure(
        ["airtightness", "ventilationSystem"],
        scenarios,
        situation,
        onlyShowDifferences,
      )}
    >
      <Table
        className={isOpen ? "open" : "closed"}
        sx={{ borderSpacing: "2rem 0" }}
        variant="compare"
      >
        <CategoryHeader
          isOpen={isOpen}
          onToggle={onToggle}
        >
          Ventilatie
        </CategoryHeader>
        <Tbody>
          {hasMeasure(
            "ventilationSystem",
            scenarios,
            situation,
            onlyShowDifferences,
          ) && (
            <Tr
              className={
                highlightDifferences &&
                scenarioValuesDiffer("ventilationSystem")
                  ? "highlight"
                  : ""
              }
              data-id="measure-ventilationSystem"
            >
              <Td>Type ventilatie</Td>
              <Td textAlign="center">{situation.ventilationSystem?.name}</Td>
              {scenarios.map((scenario) => (
                <Td key={scenario.id} textAlign="center">
                  {scenario.ventilationSystem?.name}
                </Td>
              ))}
            </Tr>
          )}
          {hasMeasure(
            "airtightness",
            scenarios,
            situation,
            onlyShowDifferences,
          ) && (
            <Tr
              className={
                highlightDifferences && scenarioValuesDiffer("airtightness")
                  ? "highlight"
                  : ""
              }
              data-id="measure-airtightness"
            >
              <Td>Luchtdichtheid qv10-waarde</Td>
              <Td textAlign="center">
                <FixedNumberDisplay
                  decimals={2}
                  number={situation.airtightness ?? 0}
                />
              </Td>
              {scenarios.map((scenario) => (
                <Td key={scenario.id} textAlign="center">
                  <FixedNumberDisplay
                    decimals={2}
                    number={scenario.airtightness ?? 0}
                  />
                </Td>
              ))}
            </Tr>
          )}
        </Tbody>
        <CategoryFooter
          calculateCO2Impact={tempCalculateCO2Impact}
          calculatePrice={tempCalculatePrice}
          taxKey={TaxKey.Ventilation}
        />
      </Table>
    </Collapse>
  );
}
